import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import { t } from 'i18next';
import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { createPaymentTransaction } from 'src/shared/services/paymentService';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledDatePicker from 'src/shared/components/form/controlledDatePicker';
import ControlledSelectBox from 'src/shared/components/form/controlledSelectBox';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { DialogCloseButton } from 'src/shared/components/organisms/dialogCloseButton';
import dayjs from 'dayjs';
import { PaymentTransactionPost } from '../models/createTransaction';

type PaymentTransactionCreateModalProps = {
  modalOpen: boolean;
  initialValues?: PaymentTransactionPost;
  onSave: () => void;
  onClose: () => void;
};

export const PaymentTransactionCreateModal = (props: PaymentTransactionCreateModalProps) => {
  const { modalOpen, initialValues = undefined, onSave, onClose } = props;

  const isMounted = useIsMounted();

  const componentId = `dialog-${Date.now().toString(36) + Math.random().toString(36).substring(2)}`;

  const requiredMessage = t('common:validationMessages.required');

  const PaymentTransactionPostSchema: Schema<PaymentTransactionPost> = yup.object().shape({
    ocr: yup.string().min(5, 'Minst fem tecken').required(requiredMessage),
    date: yup.string().required(requiredMessage),
    amount: yup.number().min(1, requiredMessage).required(requiredMessage),
    source: yup.string().required(requiredMessage),
    sender: yup.string().optional(),
    comment: yup.string().optional(),
  });

  const formMethods = useForm<PaymentTransactionPost>({
    resolver: yupResolver(PaymentTransactionPostSchema),
    mode: 'all',
    defaultValues: {
      ocr: (initialValues && initialValues.ocr) || '0',
      date: (initialValues && initialValues.date) || null,
      amount: (initialValues && initialValues.amount) || 0,
      source: (initialValues && initialValues.source) || '',
      sender: (initialValues && initialValues.sender) || '',
      comment: (initialValues && initialValues.comment) || '',
    },
  });

  const {
    handleSubmit,
    trigger,
    formState: { isSubmitting },
  } = formMethods;

  const onHandleSave = useCallback(() => {
    onSave();
  }, [onSave]);

  const onHandleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const onSubmit = async (data: PaymentTransactionPost) => {
    const dataToPost: PaymentTransactionPost = {
      ...data,
      date: dayjs(data.date).format('YYYY-MM-DDT00:00:00'),
      sender: 'FS',
    };
    ConsoleHelper.log('onSubmit data: ', dataToPost);
    await trigger();
    try {
      await createPaymentTransaction(dataToPost);
      if (isMounted()) {
        toast.success(t('invoice:transactionPage.createTransactionSuccess'));
        onHandleSave();
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    }
  };

  return (
    <Dialog open={modalOpen} onClose={onHandleClose} maxWidth='md' fullWidth>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle sx={{ m: 0, p: 2 }} id={`${componentId}-titel`}>
            <Box sx={{ lineHeight: 1.2 }}>{t('invoice:transactionPage.createTransactionHeader')}</Box>
            <DialogCloseButton handleClose={onHandleClose} />
          </DialogTitle>
          <DialogContent dividers sx={{ p: 2 }}>
            <GridWrapper narrow>
              <Grid item xs={12} sm={4} md={4}>
                <ControlledDatePicker label={t('invoice:transactionPage.date')} name='date' />
              </Grid>
              <Grid item xs>
                <ControlledTextField name='ocr' label={t('invoice:transactionPage.ocr')} />
              </Grid>
              <Grid item xs>
                <ControlledTextField name='amount' label={t('invoice:transactionPage.amount')} inputProps={{ maxLength: 7 }} />
              </Grid>
              <Grid item xs>
                <ControlledSelectBox
                  items={[
                    { label: t('invoice:transactionPage.source.bg'), value: 'Bankgiro' },
                    { label: t('invoice:transactionPage.source.pg'), value: 'Plusgiro' },
                    { label: t('invoice:transactionPage.source.cash'), value: 'Kassa' },
                  ]}
                  name='source'
                  label={t('invoice:transactionPage.source.label')}
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField name='comment' label={t('invoice:transactionPage.comment')} rows={3} multiline />
              </Grid>
            </GridWrapper>
          </DialogContent>
          <DialogActions sx={{ p: 1 }}>
            <Button variant='outlined' color='primary' disableElevation onClick={onHandleClose}>
              {t('common:button.cancel')}
            </Button>
            <LoadingButton autoFocus loading={isSubmitting} variant='contained' type='submit' disableElevation>
              {t('common:button.save')}
            </LoadingButton>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
